import { Globe2, Users2, Notebook, Cog } from "lucide-react";
import PageHero from "@/components/PageHero";
import SectionHeader from "@/components/SectionHeader";
import { PrimaryCTA } from "@/components/CTAButtons";

const DIFFERENTIATORS = [
  {
    Icon: Notebook,
    title: "Intelligence-led sourcing model",
    body:
      "We don't sell access to a database. We sell decisions — analyst-written, evidence-backed, and accountable.",
  },
  {
    Icon: Globe2,
    title: "Cross-border relationship network",
    body:
      "FFIB's network spans buyers and suppliers across multiple regions, with active relationships kept current.",
  },
  {
    Icon: Users2,
    title: "Conference-driven market knowledge",
    body:
      "We invest heavily in being present where the industry meets — and capture what we learn in the platform.",
  },
  {
    Icon: Cog,
    title: "Internal workflow built around outcomes",
    body:
      "The IngredientSignal platform is the engine our analysts use to deliver tighter shortlists and sharper reports.",
  },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero
        eyebrow="About"
        title={
          <>
            A subsidiary of FFIB,
            <br />
            built for{" "}
            <span className="text-brand-olive">specialty ingredients</span>.
          </>
        }
        subtitle="IngredientSignal is the intelligence-led sourcing and matchmaking initiative of Food & Feed Ingredients Biz — applying FFIB's cross-border network and analyst rigour specifically to the specialty ingredients market."
      />

      {/* FFIB background */}
      <section className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionHeader
              eyebrow="Parent Company"
              title="Food & Feed Ingredients Biz (FFIB)."
            />
          </div>
          <div className="lg:col-span-7 space-y-6 text-brand-warm leading-relaxed font-light">
            <p>
              FFIB is a premium B2B sourcing, importing, and distribution
              partner serving the food, beverage, nutraceutical, and animal
              feed industries. We operate across borders, build long-term
              counterparty relationships, and treat every sourcing decision
              as a serious commercial commitment.
            </p>
            <p>
              IngredientSignal was created to put that operating model behind
              a clearly defined service: curated, intelligence-led sourcing
              for clients who would rather receive verified options than search
              through directories.
            </p>
          </div>
        </div>
      </section>

      {/* What makes us different */}
      <section className="py-24 lg:py-32 bg-brand-linen border-y border-brand-border">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionHeader
            eyebrow="What Makes Us Different"
            title="Intelligence, not access."
            intro="Four commitments shape every IngredientSignal engagement."
          />
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6">
            {DIFFERENTIATORS.map((d) => (
              <div
                key={d.title}
                data-testid={`about-diff-${d.title.toLowerCase().split(" ")[0]}`}
                className="bg-brand-cream border border-brand-border p-10 hover:border-brand-muted/40 transition-colors"
              >
                <d.Icon
                  className="w-6 h-6 text-brand-olive"
                  strokeWidth={1.4}
                />
                <h3 className="font-display mt-6 text-xl text-brand-deep font-medium leading-snug">
                  {d.title}
                </h3>
                <p className="mt-4 text-brand-muted leading-relaxed font-light">
                  {d.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Principle stripe */}
      <section className="py-24">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="border-l-2 border-brand-olive pl-6">
            <p className="font-display text-2xl md:text-3xl text-brand-deep font-light leading-snug">
              We believe sourcing decisions are too important to be left to
              open marketplaces, scraped directories, and self-serve dashboards.
              They deserve analysts, relationships, and accountability.
            </p>
            <p className="mt-6 text-sm text-brand-quiet font-mono tracking-wide">
              — IngredientSignal · A subsidiary of FFIB
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 border-t border-brand-border bg-brand-linen/70">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <h2 className="font-display text-3xl sm:text-4xl text-brand-deep font-light leading-tight">
            Want to brief FFIB?
          </h2>
          <p className="mt-5 text-brand-muted max-w-2xl font-light">
            Share your sourcing question or supplier opportunity. We'll respond
            with the right next step.
          </p>
          <div className="mt-8">
            <PrimaryCTA to="/get-started" testId="about-bottom-cta">
              Get Started
            </PrimaryCTA>
          </div>
        </div>
      </section>
    </div>
  );
}
